import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import {
    Button,
    ButtonProps
} from "@/libraries/design/components/button/src/button";
import {
    Image,
    ImageProps
} from "@/libraries/design/components/image/src/image";
import { wacToken } from "@/libraries/design/foundations";
import { Row } from "@/libraries/wpl/foundations/src";

export type HeroButtonProps = {
    text: string;
} & ButtonProps;

export type HeroLayoutProps =
    | "image-left"
    | "image-right"
    | "image-both"
    | "image-below"
    | "no-image";

export type HeroBackgroundColors = "color.primary.black";

export type HeroProps = {
    heading: string;
    subheading?: string;
    callToActionProps?: HeroButtonProps;
    layout?: HeroLayoutProps;
    imageProps?: ImageProps;
    secondaryImageProps?: ImageProps;
    size?: "large" | "regular";
    backgroundColor?: HeroBackgroundColors;
};

export const Hero = ({ layout = "image-right", ...props }: HeroProps) => {
    return (
        <section
            style={
                {
                    "--background-color": `${
                        props.backgroundColor
                            ? wacToken(props.backgroundColor)
                            : "transparent"
                    }`
                } as React.CSSProperties
            }
            css={css({
                background: "var(--background-color)"
            })}
        >
            {layout === "image-both" ? (
                <HeroBothImages {...props} />
            ) : layout === "no-image" ? (
                <HeroNoImage {...props} />
            ) : layout === "image-below" ? (
                <HeroImageBelow {...props} />
            ) : (
                <HeroOneImage layout={layout} {...props} />
            )}
        </section>
    );
};
export default Hero;

const DARK_BKGD_COLORS: string[] = ["color.primary.black"];

const HeadingContent = ({
    heading,
    subheading,
    callToActionProps,
    size = "regular",
    backgroundColor
}: HeroProps) => {
    const { text: buttonText, ...buttonProps } =
        (callToActionProps as HeroButtonProps) ?? {};
    const isLarge = size === "large";

    const useLightText = backgroundColor
        ? DARK_BKGD_COLORS.includes(backgroundColor)
        : false;

    return (
        <>
            <h1
                css={headingStyle}
                style={{
                    color: useLightText
                        ? wacToken("color.neutral.light0")
                        : "inherit"
                }}
                className={isLarge ? "superhero" : undefined}
            >
                {heading}
            </h1>
            {subheading && (
                <p
                    className={isLarge ? "body-lg" : "body-md"}
                    css={bodyStyle}
                    style={{
                        color: useLightText
                            ? wacToken("color.neutral.light0")
                            : "inherit"
                    }}
                >
                    {subheading}
                </p>
            )}
            {buttonText && buttonProps && (
                <div css={buttonWrapperStyle}>
                    <Button {...buttonProps} size="large">
                        {buttonText}
                    </Button>
                </div>
            )}
        </>
    );
};

const HeroOneImage = ({
    layout = "image-right",
    imageProps,
    ...props
}: HeroProps) => {
    return (
        <Row layout="12">
            <div css={gridStyle}>
                <div css={layout === "image-left" && imageStartStyle}>
                    <HeadingContent {...props} />
                </div>
                {imageProps && (
                    <Image
                        maxPossibleWidth={700}
                        src={imageProps.src}
                        alt={imageProps.alt ?? ""}
                        loading="eager"
                        width={imageProps.width}
                        height={imageProps.height}
                    />
                )}
            </div>
        </Row>
    );
};

const gridStyle = css({
    display: "grid",
    gap: token("space.300"),
    paddingBlock: token("space.500"),
    textAlign: "center",
    alignItems: "center",

    [media.above.sm]: {
        gridTemplateColumns: "1fr 1fr",
        gridAutoFlow: "dense",
        gap: token("space.400"),
        textAlign: "start"
    },
    [media.only.sm]: {
        paddingBlock: token("space.600")
    },
    [media.above.md]: {
        paddingBlock: token("space.800")
    }
});
const headingStyle = css({
    margin: token("space.0")
});

const imageStartStyle = css({
    [media.above.sm]: {
        gridColumn: 2
    }
});
const bodyStyle = css({
    marginBlockStart: token("space.100"), // 8px between heading and subheading
    marginBlockEnd: token("space.0")
});
const buttonWrapperStyle = css({
    paddingBlockStart: token("space.400") // 32px between subheading and button
});

/*
This is an experimental start to support the needs of the Hero variation with images on the sides, primary content centered
Initial support is seen on Product enterprise pages (such as /software/jira/enterprise) as served by WAC Authoring Engine

TODO: We need to understand the levels of configurability desired/needed to support a variety of use cases, collab with design.
  Currently it is very tailored to the aforementioned enterprise experience. 
  We may need to ensure we use Image so that we can add an alt text if needed.
*/
const HeroBothImages = ({
    imageProps,
    secondaryImageProps,
    ...props
}: HeroProps) => {
    return (
        <div css={css({ overflowX: "hidden" })}>
            <Row layout="12">
                <div
                    css={[
                        css({
                            "--bg-hero-image-start": imageProps
                                ? `url(${imageProps.src})`
                                : "none",
                            "--bg-hero-image-end": secondaryImageProps
                                ? `url(${secondaryImageProps.src})`
                                : "none"
                        }),
                        gridTwoImagesStyle
                    ]}
                >
                    <HeadingContent {...props} size="regular" />
                </div>
            </Row>
        </div>
    );
};

const gridTwoImagesStyle = css({
    "position": "relative",
    "display": "grid",
    "justifySelf": "center",
    "textAlign": "center",
    "placeItems": "center",
    "paddingBlock": token("space.500"),
    [media.above.sm]: {
        width: "50%", // we do not have a grid for 3-6-3 which this pattern uses, so we handle it as customer here
        paddingInline: token("space.100")
    },
    [media.only.sm]: {
        paddingBlock: token("space.600")
    },
    [media.above.md]: {
        paddingBlock: token("space.800")
    },
    "::before": {
        [media.above.sm]: {
            content: "",
            position: "absolute",
            insetInlineEnd: "100%",
            backgroundImage: "var(--bg-hero-image-start)",
            backgroundRepeat: "no-repeat",
            backgroundSize: "contain",
            backgroundPosition: "100% 50%"
        },
        [media.only.sm]: {
            insetBlock: 0,
            width: "75%"
        },
        [media.only.md]: {
            insetBlock: 0,
            width: "75%"
        },
        [media.above.lg]: {
            insetBlock: "10%",
            width: "100%"
        }
    },
    "::after": {
        [media.above.sm]: {
            content: "",
            position: "absolute",
            backgroundImage: "var(--bg-hero-image-end)",
            backgroundRepeat: "no-repeat",
            backgroundSize: "contain",
            backgroundPosition: "0% 50%"
        },
        [media.only.sm]: {
            insetBlock: 0,
            width: "75%",
            insetInlineEnd: "-75%"
        },
        [media.only.md]: {
            insetBlock: 0,
            width: "75%",
            insetInlineEnd: "-75%"
        },
        [media.above.lg]: {
            insetBlock: "10%",
            width: "100%",
            insetInlineEnd: "-100%"
        }
    }
});

const HeroNoImage = (props: HeroProps) => {
    return (
        <Row layout="10">
            <div css={[centerTextWrapperStyle, noImageBottomPaddingStyle]}>
                <HeadingContent {...props} />
            </div>
        </Row>
    );
};

const centerTextWrapperStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    paddingBlock: token("space.500"),
    [media.only.sm]: {
        paddingBlock: token("space.600")
    },
    [media.above.md]: {
        paddingBlock: token("space.800")
    }
});

const noImageBottomPaddingStyle = css({
    paddingBlockEnd: token("space.800")
});

const HeroImageBelow = ({ imageProps, ...props }: HeroProps) => {
    return (
        <Row layout="10">
            <div css={imageBelowContainerStyle}>
                <div css={centerTextWrapperStyle}>
                    <HeadingContent {...props} />
                </div>
                {imageProps && (
                    <div css={imageBelowImageWrapperStyle}>
                        <Image
                            src={imageProps.src}
                            alt={imageProps.alt ?? ""}
                            loading="eager"
                            width={imageProps.width}
                            height={imageProps.height}
                        />
                    </div>
                )}
            </div>
        </Row>
    );
};

const imageBelowContainerStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    paddingBlockEnd: token("space.800")
});

const imageBelowImageWrapperStyle = css({
    "width": "100%",
    "marginBlockStart": token("space.400"),
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "& figure": {
        width: "100%"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "& img": {
        width: "100%",
        display: "block"
    }
});
